import type { BlogPostPreview } from "../schema/api/resources";
import type { ImageSource } from "../schema/image";

/**
 * Extract a YouTube video id from any common URL shape:
 *   - https://www.youtube.com/watch?v=ID
 *   - https://youtu.be/ID
 *   - https://www.youtube.com/embed/ID
 *   - https://www.youtube.com/v/ID
 */
export function extractYouTubeVideoId(videoUrl: string): string | null {
    try {
        const url = new URL(videoUrl);
        const hostname = url.hostname.toLowerCase();
        if (hostname.includes("youtu.be")) {
            return url.pathname.slice(1) || null;
        }
        const embedMatch = url.pathname.match(/\/(?:embed|v)\/([^/?]+)/);
        if (embedMatch) return embedMatch[1];
        return url.searchParams.get("v");
    } catch {
        return null;
    }
}

/**
 * Extract a Vimeo numeric video id from `vimeo.com/ID` or
 * `player.vimeo.com/video/ID` URLs.
 */
export function extractVimeoVideoId(videoUrl: string): string | null {
    try {
        const url = new URL(videoUrl);
        const segments = url.pathname.split("/").filter(Boolean);
        const id = segments[segments.length - 1];
        return id && /^\d+$/.test(id) ? id : null;
    } catch {
        return null;
    }
}

/**
 * Normalize an Apple Podcasts URL to the embed host. Accepts both
 * `https://podcasts.apple.com/...` and the already-prefixed
 * `https://embed.podcasts.apple.com/...`. Returns null if the URL doesn't
 * point at podcasts.apple.com or doesn't contain the expected `/id<digits>`
 * podcast segment.
 */
export function normalizeApplePodcastEmbedUrl(podcastUrl: string): string | null {
    try {
        const url = new URL(podcastUrl);
        const host = url.hostname.toLowerCase();
        const isAppleHost = host === "podcasts.apple.com" || host === "embed.podcasts.apple.com";
        if (!isAppleHost) return null;
        // Require an /id<digits> segment so we don't embed unrelated apple.com pages.
        if (!/\/id\d+/.test(url.pathname)) return null;
        url.hostname = "embed.podcasts.apple.com";
        return url.toString();
    } catch {
        return null;
    }
}

/**
 * Best-effort still thumbnail for a preview, so feed cards can render a
 * consistent image without embedding the full video player. YouTube derives
 * from the video id; Vimeo requires an oEmbed hop and is left for the stats
 * refresh cron to populate as a separate cache — callers should treat a null
 * result as "fall back to a placeholder tile".
 */
export function getBlogPostPreviewThumbnail(
    preview: BlogPostPreview | null | undefined,
): string | null {
    if (!preview) return null;
    switch (preview.type) {
        case "none":
            return null;
        case "image-url":
        case "image-asset":
            return preview.url;
        case "youtube-embed": {
            const id = extractYouTubeVideoId(preview.url);
            return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
        }
        case "vimeo-embed":
        case "video-url":
        case "video-asset":
        case "apple-podcast-embed":
            return null;
    }
}

/**
 * PRTL-1349: the preview thumbnail as an image source — with the asset's WebP
 * size variants when it has them, so cards can use `srcset` (web) or pick one
 * size (navigator). Same null rule as getBlogPostPreviewThumbnail.
 */
export function getBlogPostPreviewImage(
    preview: BlogPostPreview | null | undefined,
): ImageSource | null {
    if (preview?.type === "image-asset" && preview.image) return preview.image;
    const src = getBlogPostPreviewThumbnail(preview);
    return src ? { src } : null;
}

export function isVideoPreview(preview: BlogPostPreview | null | undefined): boolean {
    if (!preview) return false;
    return (
        preview.type === "video-url" ||
        preview.type === "video-asset" ||
        preview.type === "youtube-embed" ||
        preview.type === "vimeo-embed"
    );
}

/**
 * True if this preview should render as a playable/interactive embed in the
 * card itself (lite-pattern video, hosted video, podcast iframe), as opposed
 * to a static image or no preview.
 */
export function isEmbedPreview(preview: BlogPostPreview | null | undefined): boolean {
    if (!preview) return false;
    return (
        preview.type === "video-url" ||
        preview.type === "video-asset" ||
        preview.type === "youtube-embed" ||
        preview.type === "vimeo-embed" ||
        preview.type === "apple-podcast-embed"
    );
}

/**
 * Format a seconds duration for display: "H:MM:SS" if an hour or more, else
 * "M:SS". Matches the legacy WP display format.
 */
export function formatPreviewDuration(totalSeconds: number): string {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Compact view-count formatter ("1.2M", "1.2K", "1234"). Matches legacy
 * WP spotlight cards.
 */
export function formatPreviewViewCount(count: number): string {
    if (count >= 1_000_000) {
        return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
    }
    if (count >= 1_000) {
        return `${(count / 1_000).toFixed(1).replace(/\.0$/, "")}K`;
    }
    return count.toString();
}
